import { useMemo } from 'react';
import { AnyResourceDefinition, ResourceDefinition } from '../resources/types';
import { FunctionalState, NoParams } from '../resolvers/definition/resolver';
import { ResourceClient, ResourceInstanceState } from '../client/types';
import { emptyInitialParams, usePulseClient } from './shared';
import { ResolverState } from '../resolvers/definition/resolverState';
import { useKey } from './useKey';

type UnboundActions = {
  getState: ResourceClient['getState'];
  getStates: ResourceClient['getStates'];
  setState: ResourceClient['setState'];
  invalidate: ResourceClient['invalidate'];
  invalidateAll: ResourceClient['invalidateAll'];
  retain: ResourceClient['retain'];
};

type BoundActions<TParams, TData, TError extends Error = Error> = {
  getState: () => ResolverState<TData, TError> | undefined;
  getStates: () => ResourceInstanceState<TParams, TData, TError>[];
  setState: (state: FunctionalState<TData, TError>) => void;
  invalidate: () => void;
  invalidateAll: () => void;
  retain: (duration?: number) => void;
};

export function useResourceActions(): UnboundActions;
export function useResourceActions<
  TParams,
  TData,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options: { params: { [K in keyof TParams]: TParams[K] } }
): BoundActions<TParams, TData, TError>;
export function useResourceActions<
  TParams extends NoParams,
  TData,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options?: { params?: NoParams }
): BoundActions<TParams, TData, TError>;

export function useResourceActions(
  resource?: AnyResourceDefinition,
  options?: { params?: any }
): UnboundActions | BoundActions<any, any, any> {
  const client = usePulseClient();

  const params = options?.params ?? emptyInitialParams;

  const instanceKey = useKey(resource, params);

  return useMemo(() => {
    if (!resource) {
      return client;
    }

    return {
      getState: () => client.getState({ resource, params, instanceKey }),
      getStates: () => client.getStates({ resource }),
      setState: (state: any) =>
        client.setState({
          resource,
          params,
          instanceKey,
          state,
        }),
      invalidate: () => client.invalidate({ resource, params, instanceKey }),
      invalidateAll: () => client.invalidateAll({ resource }),
      retain: (duration?: number) =>
        client.retain({ resource, params, instanceKey, duration }),
    };
    // instanceKey is a perfect proxy for params and resource changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, instanceKey]);
}
